import { Card, Skeleton } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { PersonaCard } from '@/entities/persona';
import { selectDistinctiveTraits } from '../../lib';
import type { AudienceTableData } from '../../model';
import { AudienceStatusChip } from '../AudienceStatusChip';
import type { AudiencesGridV2Props } from './AudiencesGridV2.types';

/** Enough to fill the widest row the grid draws, so the pane never looks short. */
const SKELETON_COUNT = 4;

export function AudiencesGridV2({
  personas,
  isLoading = false,
  gwiEnabled,
  onPersonaClick,
  onEdit,
  onDelete,
}: AudiencesGridV2Props) {
  const { t } = useTranslation('audiences');

  const buildActions = (persona: AudienceTableData) => {
    const actions = [];
    // A persona still being generated has nothing settled to edit yet.
    const isReady = persona.creationStatus === 'ready';
    const isDefault = persona.type === 'Default';

    if (onEdit) {
      actions.push({
        label: t('audiences.audiencesGridV2.edit'),
        onSelect: () => onEdit(persona),
        disabled: !isReady || isDefault,
        ...(isDefault && {
          disabledReason: t('audiences.audiencesGridV2.defaultCannotBeEdited'),
        }),
      });
    }
    if (onDelete) {
      actions.push({
        label: t('audiences.audiencesGridV2.delete'),
        onSelect: () => onDelete(persona),
        ...(isDefault && {
          disabled: true,
          disabledReason: t('audiences.audiencesGridV2.defaultCannotBeDeleted'),
        }),
      });
    }

    return actions;
  };

  return (
    <div
      data-testid="audiences-grid"
      className="grid grid-cols-1 gap-grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      {isLoading &&
        Array.from({ length: SKELETON_COUNT }).map((_, index) => (
          <Card
            key={`audience-skeleton-${index}`}
            variant="outline"
            data-testid="audiences-grid-skeleton"
            className="gap-md rounded-lg border-border-light p-md shadow-none"
          >
            <Skeleton className="h-40 w-full rounded-md" />
            <div className="flex flex-col gap-2xs">
              <Skeleton className="h-5 w-3/5" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          </Card>
        ))}
      {!isLoading &&
        personas.map((persona) => (
          <PersonaCard
            key={persona.id}
            title={persona.personaTitle || persona.segmentName}
            tags={selectDistinctiveTraits(persona, gwiEnabled).map((trait) =>
              t('audiences.audiencesGridV2.trait', {
                label: trait.label,
                multiplier: trait.multiplier,
              }),
            )}
            badge={<AudienceStatusChip status={persona.creationStatus} />}
            photoAssetId={persona.photoAssetId}
            mouseOverAssetId={persona.mouseOverAssetId}
            onClick={() => onPersonaClick(persona)}
            actions={buildActions(persona)}
          />
        ))}
    </div>
  );
}

AudiencesGridV2.displayName = 'AudiencesGridV2';
